import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMiniERP } from './hooks/useMiniERP';
import { useOfflineCounterMemo } from './hooks/useOfflineSync';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import { isVoiceSupported, useVoiceMemoAssistant, useSingleFieldDictation, VOICE_FIELD_SEQUENCE } from './components/MiniERPVoice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faWifi, faCloudUploadAlt, faCloudDownloadAlt, faCheckCircle,
  faExclamationTriangle, faSpinner, faSignal, faTrash, faGlobe, faMobileAlt,
  faMicrophone, faMicrophoneSlash, faVolumeUp, faVolumeMute, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

const emptyForm = {
  type: 'Sales', itemName: '', amount: '', qty: '', unit: '',
  paymentType: 'Cash', isPaid: true, customerName: '', customerMobile: '', remarks: '',
};

// Deliberately the simplest version of Quick Counter Memo — Customer-only
// (no Business/Barter — those need live EDI account lookups that can't work
// offline), built for low-end Android / patchy-signal use.
const MiniERPOffline = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const userId = authState?.user?._id;
  const { createPettyTransaction, fetchPettyTransactions } = useMiniERP();
  const { status, showSuccess, showError } = useStatusBanner();

  const {
    browserOnline, records, unsyncedCount, uploading, downloading,
    submitEntry, uploadToOnline, downloadFromOnline, clearOffline, isSupported,
  } = useOfflineCounterMemo({ createPettyTransaction, fetchPettyTransactions, userId });

  const [form, setForm] = useState(emptyForm);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [forceOffline, setForceOffline] = useState(
    localStorage.getItem("FORCE_OFFLINE") === "1"
  );

  // const toggleForceOffline = () => {
  //   const next = !forceOffline;
  //   setForceOffline(next);

  //   if (next) {
  //     localStorage.setItem("FORCE_OFFLINE", "1");
  //   } else {
  //     localStorage.removeItem("FORCE_OFFLINE");
  //   }

  //   window.location.reload();
  // };
  const toggleForceOffline = () => {
    const next = !forceOffline;

    setForceOffline(next);

    if (next) {
      localStorage.setItem("FORCE_OFFLINE", "1");
    } else {
      localStorage.removeItem("FORCE_OFFLINE");
    }

    window.dispatchEvent(new Event("storage"));
  };

  const submitOfflineForm = async (viaVoice = false) => {
    if (!form.itemName || !form.amount) {
      showError(viaVoice ? "I didn't catch the item or amount — try again." : 'Item name and amount are required.');
      return;
    }
    const payload = {
      ...form,
      amount: Number(form.amount),
      qty: form.qty ? Number(form.qty) : undefined,
      unit: form.qty ? form.unit : undefined,
      date: new Date(),
    };
    try {
      const result = await submitEntry(payload);
      if (result.queued) {
        showSuccess(viaVoice ? '🎤📴 No connection — saved on this device, will sync automatically.' : '📴 No connection right now — saved on this device. Will sync automatically once you\'re back online.');
      } else {
        showSuccess(viaVoice ? '🎤✅ Saved and synced.' : '✅ Saved and synced.');
      }
      setForm(emptyForm);
    } catch (err) {
      showError(err.message);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); submitOfflineForm(false); };

  // ================= VOICE — same assistant as Home, ported here =================
  const voice = useVoiceMemoAssistant({
    onWake: () => setForm(emptyForm),
    onFieldUpdate: (key, value) => setForm((f) => ({ ...f, [key]: value })),
    onSave: () => submitOfflineForm(true),
    onCancel: () => {},
  });

  const fieldMic = useSingleFieldDictation();
  const dictateField = (fieldKey) => {
    fieldMic.startListening((text) => {
      const spec = VOICE_FIELD_SEQUENCE.find((f) => f.key === fieldKey);
      const value = spec ? spec.parse(text) : text;
      setForm((f) => ({ ...f, [fieldKey]: value }));
    });
  };

  const MicButton = ({ fieldKey }) => {
    if (!isVoiceSupported) return null;
    return (
      <button
        type="button"
        onClick={() => dictateField(fieldKey)}
        className={`w-fit px-1.5 py-1.5 rounded-lg text-xs transition-colors ${
          fieldMic.listening ? 'bg-rose-600 text-white animate-pulse' : 'bg-gray-600 text-gray-300 hover:bg-cyan-600 hover:text-white'
        }`}
        title="Speak this field"
      >
        <FontAwesomeIcon icon={faMicrophone} />
      </button>
    );
  };

  // ================= CLEAR OFFLINE — guarded confirm =================
  const handleClearClick = () => setConfirmingClear(true);

  const handleClearConfirm = async () => {
    await clearOffline();
    setConfirmingClear(false);
    showSuccess('Offline data cleared from this device. Your online records are untouched.');
  };

  // ================= STATUS BADGE =================
  const statusBadge = (r) => {
    if (r.origin === 'online') {
      return <span className="flex items-center gap-1 text-xs text-cyan-400"><FontAwesomeIcon icon={faGlobe} /> Online</span>;
    }
    const map = {
      pending: { icon: faSignal, color: 'text-amber-400', label: 'On device, waiting' },
      syncing: { icon: faSpinner, color: 'text-cyan-400', label: 'Syncing...', spin: true },
      synced: { icon: faCheckCircle, color: 'text-emerald-400', label: 'Synced' },
      failed: { icon: faExclamationTriangle, color: 'text-rose-400', label: 'Failed' },
    };
    const m = map[r.syncStatus] || map.pending;
    return <span className={`flex items-center gap-1 text-xs ${m.color}`}><FontAwesomeIcon icon={m.icon} spin={!!m.spin} /> <FontAwesomeIcon icon={faMobileAlt} /> {m.label}</span>;
  };

  if (!isSupported) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 p-6 text-center">
        <p className="text-rose-400 mt-10">This device's browser doesn't support offline storage — please use the regular Home page instead.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4">
      <div className="max-w-lg mx-auto">
        {/* Connection status */}
        <div className={`flex items-center justify-between rounded-xl px-4 py-2 mb-3 text-sm ${
            browserOnline
              ? 'bg-emerald-950/40 border border-emerald-700 text-emerald-300'
              : 'bg-amber-950/40 border border-amber-700 text-amber-300'
          }`}>
            <span className="flex items-center gap-2">
              <FontAwesomeIcon icon={faWifi} />
              {browserOnline ? 'Online' : 'No signal — entries save on this device'}
            </span>

            <button
              type="button"
              onClick={toggleForceOffline}
              className={`w-fit px-3 py-1 rounded-lg text-xs font-medium ${
                forceOffline
                  ? 'bg-rose-700 hover:bg-rose-600 text-white'
                  : 'bg-cyan-700 hover:bg-cyan-600 text-white'
              }`}
            >
              {forceOffline ? '🔴 Forced Offline' : '🟢 Normal Network'}
            </button>
          </div>

        {/* Sync action bar */}
        <div className="flex flex-wrap justify-center gap-2 mb-3">
          <button
            type="button"
            onClick={uploadToOnline}
            disabled={uploading}
            className="w-fit px-2 py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded-xl text-xs flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faCloudUploadAlt} spin={uploading} />
            Sync &amp; Upload {unsyncedCount > 0 ? `(${unsyncedCount})` : ''}
          </button>
          <button
            type="button"
            onClick={downloadFromOnline}
            disabled={downloading}
            className="w-fit px-2 py-2 bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50 rounded-xl text-xs flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faCloudDownloadAlt} spin={downloading} />
            Sync &amp; Download
          </button>
        </div>

        <p className="text-gray-500 text-[11px] flex items-start gap-1.5 mb-4">
          <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 shrink-0" />
          This device keeps only the last 30 days of entries for offline use — older records stay safely on the server.
           How much fits depends on your phone's free storage. Sales &amp; Purchase only. Works with no signal! 
        </p>

        <h1 className="text-xl font-bold text-cyan-200 mb-1">Offline Counter Entry</h1>
        {/* <p className="text-xs text-gray-500 mb-4">Sales &amp; Purchase only. Works with no signal — voice needs a connection to work.</p> */}

        {/* ================= VOICE STATUS PANEL ================= */}
        {isVoiceSupported && (
          <div className="flex flex-col justify-center mb-3">
            <button
              type="button"
              disabled={!browserOnline}
              onClick={() => (voice.armed ? voice.disarm() : voice.arm())}
              className={`w-fit px-2 py-1 rounded-xl gap-2 text-sm font-medium ${
                !browserOnline
                  ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                  : voice.armed
                  ? "bg-rose-600 hover:bg-rose-500"
                  : "bg-violet-600 hover:bg-violet-500"
              }`}
            >
              <FontAwesomeIcon
                icon={!browserOnline ? faMicrophoneSlash : (voice.armed ? faMicrophoneSlash : faMicrophone)}
              />
              {!browserOnline
                ? "🎤 Voice requires network"
                : voice.armed
                ? "Voice On"
                : "🎤 Try Voice"}
            </button>

            {voice.armed && (
              <div className={`rounded-2xl p-3 mt-2 border text-xs ${voice.dictating ? 'bg-violet-950/40 border-violet-600' : 'bg-gray-900 border-gray-700'}`}>
                {voice.micError ? (
                  <p className="text-rose-400">🎙️ {voice.micError}</p>
                ) : !voice.micActive ? (
                  <p className="text-amber-400">⏳ Starting microphone...</p>
                ) : !voice.dictating ? (
                  <p className="text-violet-300">🎤 Say <span className="font-bold">"Thinky Start"</span> to fill this hands-free</p>
                ) : (
                  <div>
                    <p className="text-white font-semibold">Step {voice.stepIndex + 1} of {voice.totalFields}: {voice.currentField?.label}</p>
                    <p className="text-violet-300 mt-1">{voice.currentField?.prompt}</p>
                    <p className="text-gray-500 mt-1">Say "Thinky Save" anytime to submit.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <StatusBanner status={status} className="mb-4" />

        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-2xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs flex items-center justify-between mb-1">Type <MicButton fieldKey="type" /></label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-sm">
                <option value="Sales">Sales</option>
                <option value="Purchase">Purchase</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs flex items-center justify-between mb-1">Payment Mode <MicButton fieldKey="paymentType" /></label>
              <select value={form.paymentType} onChange={(e) => setForm({ ...form, paymentType: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-sm">
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
                <option value="Credit">Credit</option>
                <option value="Free">Free</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-xs flex items-center justify-between mb-1">Item Name <MicButton fieldKey="itemName" /></label>
            <input type="text" value={form.itemName} onChange={(e) => setForm({ ...form, itemName: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" required />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <input type="number" placeholder="Amount ₹" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" required />
            <input type="number" placeholder="Qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />
            <input type="text" placeholder="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" disabled={!form.qty} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="Customer / Supplier" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />
            <input type="tel" placeholder="Mobile" value={form.customerMobile} onChange={(e) => setForm({ ...form, customerMobile: e.target.value.replace(/[^\d+ ]/g, '') })}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />
          </div>

          <textarea placeholder="Remarks (optional)" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            style={{ height: '70px' }} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />

          <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 rounded-xl py-3 font-medium flex items-center justify-center gap-2">
            <FontAwesomeIcon icon={faPlus} /> Save {form.type}
          </button>
        </form>

        {/* ================= MERGED LIST ================= */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-300">Last 30 Days ({records.length})</h2>
            {records.length > 0 && !confirmingClear && (
              <button type="button" onClick={handleClearClick} className="w-fit px-2 py-1 text-rose-400 hover:text-rose-300 text-xs flex items-center gap-1">
                <FontAwesomeIcon icon={faTrash} /> Clear from Offline
              </button>
            )}
          </div>

          {confirmingClear && (
            <div className="bg-rose-950/40 border border-rose-700 rounded-xl p-3 mb-3 text-xs">
              {unsyncedCount > 0 ? (
                <p className="text-rose-300 mb-2">
                  ⚠️ You have <strong>{unsyncedCount}</strong> entr{unsyncedCount === 1 ? 'y' : 'ies'} on this device not yet uploaded. Deleting now will lose {unsyncedCount === 1 ? 'it' : 'them'} permanently. Tap <strong>Sync &amp; Upload</strong> first, or confirm you're okay losing {unsyncedCount === 1 ? 'it' : 'them'}.
                </p>
              ) : (
                <p className="text-gray-300 mb-2">This will remove all offline data stored on this device. Your online records are safe and untouched. Have you confirmed your data is already synced?</p>
              )}
              <div className="flex gap-2">
                <button type="button" onClick={() => setConfirmingClear(false)} className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-lg hover:bg-gray-700">Cancel</button>
                <button type="button" onClick={handleClearConfirm} className="flex-1 w-fit px-2 py-1 bg-rose-600 hover:bg-rose-500 rounded-lg font-medium">Yes, delete from this device</button>
              </div>
            </div>
          )}

          {records.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No entries yet on this device — add one above, or tap Sync &amp; Download to pull in what's already online.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {records.map((r) => (
                <div key={r.clientId} className="bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 flex justify-between items-center text-xs">
                  <div>
                    <div className="text-white font-medium">{r.payload.itemName}{r.payload.qty ? ` × ${r.payload.qty}${r.payload.unit ? ' ' + r.payload.unit : ''}` : ''}</div>
                    <div className="text-gray-500">₹{r.payload.amount} • {new Date(r.payload.date || r.createdAt).toLocaleString()}</div>
                  </div>
                  {statusBadge(r)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiniERPOffline;